import { useState, useEffect, useCallback } from 'react'
import { ChevronRight, Folder, FolderOpen, FileText } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface TreeNode {
  name: string
  path: string       // Full path relative to workspace base, e.g. "main/.planning/ROADMAP.md"
  is_dir: boolean
  children?: TreeNode[]
  isLoaded?: boolean  // true once a directory's children have been fetched
}

interface DocsTreeProps {
  nodes: TreeNode[]
  selectedPath: string | null
  activePhase: number | null  // Parsed from STATE.md
  onSelect: (path: string) => void
  onExpand: (path: string) => void  // Called when a directory needs its children loaded
  className?: string
}

/** Well-known planning file names that should sort first among files */
const PRIORITY_FILES = ['STATE.md', 'ROADMAP.md', 'PROJECT.md', 'CONTEXT.md', 'CONFIG.md']

/**
 * Check if a directory name matches the active phase number.
 * E.g., activePhase=35 matches "35-documentation-panel"
 */
function isActivePhaseDir(dirName: string, activePhase: number | null): boolean {
  if (activePhase === null) return false
  const match = dirName.match(/^(\d+)-/)
  return match ? parseInt(match[1], 10) === activePhase : false
}

/**
 * Sort nodes: directories first, then files.
 * Within files, priority planning files come first.
 * Within each group, sort alphabetically.
 */
function sortNodes(nodes: TreeNode[]): TreeNode[] {
  return [...nodes].sort((a, b) => {
    // Directories first
    if (a.is_dir && !b.is_dir) return -1
    if (!a.is_dir && b.is_dir) return 1

    // Among files, priority planning files come first
    if (!a.is_dir && !b.is_dir) {
      const aPriority = PRIORITY_FILES.indexOf(a.name)
      const bPriority = PRIORITY_FILES.indexOf(b.name)
      if (aPriority !== -1 && bPriority === -1) return -1
      if (aPriority === -1 && bPriority !== -1) return 1
      if (aPriority !== -1 && bPriority !== -1) return aPriority - bPriority
    }

    // Alphabetical within same type
    return a.name.localeCompare(b.name)
  })
}

/**
 * Find all ancestor directory paths for a given path in the tree.
 * Used to auto-expand the tree to reveal the active phase directory.
 */
function findAncestorPaths(nodes: TreeNode[], targetName: string): string[] {
  const ancestors: string[] = []

  function walk(items: TreeNode[], pathStack: string[]): boolean {
    for (const node of items) {
      if (node.is_dir && node.name === targetName) {
        ancestors.push(...pathStack)
        ancestors.push(node.path)
        return true
      }
      if (node.is_dir && node.children && node.children.length > 0) {
        if (walk(node.children, [...pathStack, node.path])) {
          return true
        }
      }
    }
    return false
  }

  walk(nodes, [])
  return ancestors
}

function TreeItem({
  node,
  selectedPath,
  activePhase,
  expanded,
  onToggle,
  onSelect,
  onExpand,
  depth = 0,
}: {
  node: TreeNode
  selectedPath: string | null
  activePhase: number | null
  expanded: Set<string>
  onToggle: (path: string) => void
  onSelect: (path: string) => void
  onExpand: (path: string) => void
  depth?: number
}) {
  const isSelected = selectedPath === node.path
  const isExpanded = expanded.has(node.path)
  const isActivePhase = node.is_dir && isActivePhaseDir(node.name, activePhase)

  function handleClick() {
    if (node.is_dir) {
      onToggle(node.path)
      if (!node.isLoaded) {
        onExpand(node.path)
      }
    } else {
      onSelect(node.path)
    }
  }

  return (
    <li>
      <div
        onClick={handleClick}
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
        className={cn(
          'flex items-center gap-1.5 pr-2 py-1 rounded-md text-sm transition-colors cursor-pointer',
          isSelected && 'bg-primary/10 text-primary font-medium',
          isActivePhase && !isSelected && 'bg-green-500/10 text-green-600 dark:text-green-400 font-medium',
          !isSelected && !isActivePhase && 'text-muted-foreground hover:bg-secondary hover:text-foreground'
        )}
      >
        {/* Chevron for directories */}
        {node.is_dir ? (
          <ChevronRight
            className={cn(
              'w-3.5 h-3.5 flex-shrink-0 transition-transform',
              isExpanded && 'rotate-90'
            )}
          />
        ) : (
          <span className="w-3.5 flex-shrink-0" />
        )}

        {/* Active phase dot */}
        {isActivePhase && (
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
        )}

        {/* Icon */}
        {node.is_dir ? (
          isExpanded ? (
            <FolderOpen className="w-3.5 h-3.5 flex-shrink-0" />
          ) : (
            <Folder className="w-3.5 h-3.5 flex-shrink-0" />
          )
        ) : (
          <FileText className="w-3.5 h-3.5 flex-shrink-0" />
        )}

        {/* Name */}
        <span className="truncate">{node.name}</span>
      </div>

      {/* Children (if expanded and loaded) */}
      {node.is_dir && isExpanded && node.children && (
        <ul className="space-y-0.5">
          {sortNodes(node.children).map((child) => (
            <TreeItem
              key={child.path}
              node={child}
              selectedPath={selectedPath}
              activePhase={activePhase}
              expanded={expanded}
              onToggle={onToggle}
              onSelect={onSelect}
              onExpand={onExpand}
              depth={depth + 1}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

export default function DocsTree({
  nodes,
  selectedPath,
  activePhase,
  onSelect,
  onExpand,
  className,
}: DocsTreeProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // Auto-expand ancestors of active phase directory when activePhase or nodes change
  useEffect(() => {
    if (activePhase === null || nodes.length === 0) return

    // Find the active phase directory name pattern (e.g., "35-documentation-panel")
    // We search for any directory whose name starts with the active phase number
    function findActivePhaseTarget(items: TreeNode[]): string | null {
      for (const node of items) {
        if (node.is_dir && isActivePhaseDir(node.name, activePhase)) {
          return node.name
        }
        if (node.is_dir && node.children) {
          const found = findActivePhaseTarget(node.children)
          if (found) return found
        }
      }
      return null
    }

    const targetName = findActivePhaseTarget(nodes)
    if (targetName) {
      const ancestors = findAncestorPaths(nodes, targetName)
      if (ancestors.length > 0) {
        setExpanded((prev) => {
          const next = new Set(prev)
          for (const a of ancestors) {
            next.add(a)
          }
          return next
        })
      }
    }
  }, [activePhase, nodes])

  const handleToggle = useCallback((path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(path)) {
        next.delete(path)
      } else {
        next.add(path)
      }
      return next
    })
  }, [])

  return (
    <nav className={className}>
      <ul className="space-y-0.5 p-2">
        {sortNodes(nodes).map((node) => (
          <TreeItem
            key={node.path}
            node={node}
            selectedPath={selectedPath}
            activePhase={activePhase}
            expanded={expanded}
            onToggle={handleToggle}
            onSelect={onSelect}
            onExpand={onExpand}
          />
        ))}
      </ul>
    </nav>
  )
}
